/* bsb-size-sheet.js — mobile size picker: a trigger button that opens a
   bottom sheet. Sheet rows are <label for> tags pointing at the same
   hidden radios the desktop circle buttons use (see bsb-size-selector.liquid)
   — selecting a row is native label/radio behavior, not custom-handled here.
   This file only owns: opening/closing the sheet, keeping the trigger
   button's text and the sheet's "selected" row in sync with whichever
   radio is checked, and dialog accessibility (focus trap, Escape, restoring
   focus to the trigger on close). */

(function () {
  'use strict';

  function formatPrice(cents) {
    const amount = cents / 100;
    const hasFraction = amount % 1 !== 0;
    return '$' + amount.toLocaleString(undefined, {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }

  function init() {
    const trigger = document.getElementById('bsb-size-sheet-trigger');
    const backdrop = document.getElementById('bsb-size-sheet-backdrop');
    const sheet = document.getElementById('bsb-size-sheet');
    const triggerText = document.getElementById('bsb-size-sheet-trigger-text');
    const handle = document.getElementById('bsb-size-sheet-handle');
    const closeBtn = document.getElementById('bsb-size-sheet-close');

    if (!trigger || !backdrop || !sheet) return;

    const rowsContainer = sheet.querySelector('.bsb-pi__size-sheet-rows');
    const radios = Array.from(document.querySelectorAll('input[name="bsb-size"]'));

    function focusableElements() {
      /* The radios live in .bsb-pi__sizes (outside #bsb-size-sheet) — only
         their sheet-row <label> proxies are physically inside the sheet,
         and labels aren't part of the tab order. Build the trap's set from
         the radios themselves plus whatever's actually inside the sheet's
         own chrome (size-guide/help links). */
      const enabledRadios = radios.filter(function (r) { return !r.disabled; });
      const chrome = Array.from(
        sheet.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      return enabledRadios.concat(chrome);
    }

    function rowForInput(input) {
      return rowsContainer && rowsContainer.querySelector('label[for="' + input.id + '"]');
    }

    function syncFromInput(input) {
      const row = rowForInput(input);
      if (!row) return;

      const titleEl = row.querySelector('.bsb-pi__size-sheet-row-title');
      const price = input.getAttribute('data-price');
      const compareAtPrice = input.getAttribute('data-compare-at-price');
      if (triggerText && titleEl && price) {
        const priceNum = parseInt(price, 10);
        const compareNum = parseInt(compareAtPrice, 10);
        const onSale = compareNum > priceNum;
        triggerText.innerHTML = titleEl.textContent + ' — ' +
          (onSale ? '<s class="bsb-pi__size-sheet-trigger-compare">' + formatPrice(compareNum) + '</s> ' : '') +
          formatPrice(priceNum);
      }

      if (rowsContainer) {
        rowsContainer.querySelectorAll('.bsb-pi__size-sheet-row').forEach(function (r) {
          r.classList.toggle('is-selected', r === row);
        });
      }
    }

    function refreshPrices() {
      radios.forEach(function (input) {
        const row = rowForInput(input);
        const priceEl = row && row.querySelector('.bsb-pi__size-sheet-row-price');
        const price = input.getAttribute('data-price');
        const compareAtPrice = input.getAttribute('data-compare-at-price');
        if (!row || !priceEl || !price) return;
        const priceNum = parseInt(price, 10);
        const compareNum = parseInt(compareAtPrice, 10);
        const onSale = compareNum > priceNum;
        priceEl.classList.toggle('bsb-pi__size-sheet-row-price--sale', onSale);
        priceEl.innerHTML =
          '<span class="bsb-pi__size-sheet-row-price-compare">' + formatPrice(onSale ? compareNum : priceNum) + '</span>' +
          '<span class="bsb-pi__size-sheet-row-price-current">' + formatPrice(priceNum) + '</span>';
      });
    }

    function openSheet() {
      refreshPrices();
      document.body.style.overflow = 'hidden';
      backdrop.classList.add('is-open');
      sheet.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');

      const checked = radios.find(function (r) { return r.checked; });
      const focusTarget = (checked && rowForInput(checked)) ? checked : focusableElements()[0];
      if (focusTarget) focusTarget.focus();

      document.addEventListener('keydown', onKeydown);
    }

    function closeSheet() {
      document.body.style.overflow = '';
      backdrop.classList.remove('is-open');
      sheet.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKeydown);
      trigger.focus();
    }

    function isOpen() {
      return sheet.classList.contains('is-open');
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSheet();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = focusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    trigger.addEventListener('click', function () {
      if (isOpen()) {
        closeSheet();
      } else {
        openSheet();
      }
    });

    backdrop.addEventListener('click', closeSheet);

    if (closeBtn) closeBtn.addEventListener('click', closeSheet);

    if (handle) {
      handle.addEventListener('click', function () {
        if (isOpen()) closeSheet();
      });

      /* Drag the handle down to dismiss. Only the handle is draggable —
         the rest of the sheet scrolls its own content normally. */
      const DRAG_CLOSE_THRESHOLD = 80;
      let dragStartY = null;

      handle.addEventListener('pointerdown', function (e) {
        if (!isOpen()) return;
        dragStartY = e.clientY;
        sheet.style.transition = 'none';
        handle.setPointerCapture(e.pointerId);
      });

      handle.addEventListener('pointermove', function (e) {
        if (dragStartY == null) return;
        const delta = Math.max(0, e.clientY - dragStartY);
        sheet.style.transform = 'translateY(' + delta + 'px)';
      });

      function endDrag(e) {
        if (dragStartY == null) return;
        const delta = Math.max(0, e.clientY - dragStartY);
        dragStartY = null;
        sheet.style.transition = '';
        sheet.style.transform = '';
        if (delta > DRAG_CLOSE_THRESHOLD) closeSheet();
      }

      handle.addEventListener('pointerup', endDrag);
      handle.addEventListener('pointercancel', endDrag);
    }

    radios.forEach(function (input) {
      input.addEventListener('focus', function () {
        const row = rowForInput(input);
        if (row) row.classList.add('is-focused');
      });
      input.addEventListener('blur', function () {
        const row = rowForInput(input);
        if (row) row.classList.remove('is-focused');
      });
    });

    document.addEventListener('change', function (e) {
      if (e.target.name !== 'bsb-size') return;
      syncFromInput(e.target);
      if (isOpen()) closeSheet();
    });

    const initialChecked = radios.find(function (r) { return r.checked; });
    if (initialChecked) syncFromInput(initialChecked);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
